import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { AddressData } from "v2/store/onboardingEntityApi";
import { StepId } from "./types";
import { OnboardingState, useOnboarding } from "./useOnboarding";

export type EntityRelationKind = "parent" | "subsidiary";

export type OnboardingEntityDraft = {
  localId: string;
  entityId?: string;
  registeredName?: string;
  entityName: string;
  entitySuffix: string;
  formationTypeId: string;
  entityTypeLabel: string;
  taxedAs?: string;
  addressData: AddressData | null;
  isPrimary: boolean;
  relationKind?: EntityRelationKind;
  relatedEntityId?: string;

  cStateId?: string;

  dateOfIncorporation?: string;
};

export type OnboardingData = {
  entityTypeId: string;
  entityTypeLabel: string;
  entityTypeMain: string;
  entityTypeTaxedAs: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  profileId: string;
  groupId: string;
  entityId: string;
  companyName: string;
  entityName: string;
  entitySuffix: string;
  entities: OnboardingEntityDraft[];
  entitiesUpdating: boolean;
};

const EMPTY_DATA: OnboardingData = {
  entityTypeId: "",
  entityTypeLabel: "",
  entityTypeMain: "",
  entityTypeTaxedAs: "",
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  profileId: "",
  groupId: "",
  entityId: "",
  companyName: "",
  entityName: "",
  entitySuffix: "",
  entities: [],
  entitiesUpdating: false,
};

const STORAGE_KEY = "onboarding_v2_data";
const LEGACY_STORAGE_KEY = "v2_onboarding_data";

export const clearOnboardingDataStorage = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
};

export const readStoredOnboardingData = (): OnboardingData => {
  try {
    const stored =
      localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem(LEGACY_STORAGE_KEY);

    if (stored)
      return { ...EMPTY_DATA, ...JSON.parse(stored), entitiesUpdating: false };
  } catch {}
  return EMPTY_DATA;
};

type OnboardingDataContextValue = {
  data: OnboardingData;
  patchData: (patch: Partial<OnboardingData>) => void;
  state: OnboardingState;
};

const OnboardingDataContext = createContext<OnboardingDataContextValue | null>(
  null
);

export const OnboardingDataProvider = ({
  initialStep,
  initialDoneSteps,
  steps,
  initialData,
  children,
}: {
  initialStep: StepId;
  initialDoneSteps: StepId[];
  steps?: StepId[];
  initialData?: Partial<OnboardingData>;
  children: ReactNode;
}) => {
  const [data, setData] = useState<OnboardingData>(() => ({
    ...EMPTY_DATA,
    ...initialData,
  }));
  const state = useOnboarding({ initialStep, initialDoneSteps, steps });

  const value = useMemo(
    () => ({
      data,
      state,
      patchData: (patch: Partial<OnboardingData>) =>
        setData((prev) => {
          const next = { ...prev, ...patch };
          const isEntityCreated = Boolean(next.groupId);

          if (isEntityCreated) clearOnboardingDataStorage();
          else localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

          return next;
        }),
    }),
    [data, state]
  );

  return (
    <OnboardingDataContext.Provider value={value}>
      {children}
    </OnboardingDataContext.Provider>
  );
};

const useOnboardingContext = (): OnboardingDataContextValue => {
  const context = useContext(OnboardingDataContext);
  if (!context) {
    throw new Error(
      "useOnboarding* hooks must be used within an OnboardingDataProvider"
    );
  }
  return context;
};

export const useOnboardingData = () => {
  const { data, patchData } = useOnboardingContext();
  return { data, patchData };
};

export const useOnboardingState = (): OnboardingState =>
  useOnboardingContext().state;
